import { inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { BehaviorSubject, defer, Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import {
  AgentClient,
  AgentStatusClient,
  ApproveAgentRequest,
  SearchAgentsRequest,
  UpdateAgentRequest,
} from '../../core/openapi/bff-admin-api';
import { ISearchAgentsItem } from './agents.model';
import { LoadingState } from '../../shared/types/loading.type';
import { outputError } from '../../shared/utils/output-error.function';
import { ToastService } from '@ui-lib/components';
import { TranslateService } from '@ngx-translate/core';
import { DEFAULT_TABLE_PAGE_SIZE } from '../../shared/const/pagination.constants';

@Injectable()
export class AgentsService {
  private readonly client = inject(AgentClient);
  private readonly statusClient = inject(AgentStatusClient);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  private readonly _agentsLoadingState: WritableSignal<LoadingState> = signal<LoadingState>(
    LoadingState.IDLE,
  );
  private readonly _agentDeleteLoadingStatus: WritableSignal<LoadingState> = signal<LoadingState>(
    LoadingState.IDLE,
  );
  private readonly _agentApproveLoadingStatus: WritableSignal<LoadingState> = signal<LoadingState>(
    LoadingState.IDLE,
  );
  private readonly _agentUpdateLoadingStatus: WritableSignal<LoadingState> = signal<LoadingState>(
    LoadingState.IDLE,
  );
  private readonly _agentByIdStatusLoadingStatus: WritableSignal<LoadingState> =
    signal<LoadingState>(LoadingState.IDLE);
  private readonly _agentStatusesLoadingStatus: WritableSignal<LoadingState> = signal<LoadingState>(
    LoadingState.IDLE,
  );

  private readonly _agentsTotalCount: WritableSignal<number> = signal<number>(0);

  public readonly agentsLoadingStatus: Signal<LoadingState> = this._agentsLoadingState.asReadonly();
  public readonly agentDeleteLoadingStatus: Signal<LoadingState> =
    this._agentDeleteLoadingStatus.asReadonly();
  public readonly agentApproveLoadingStatus: Signal<LoadingState> =
    this._agentApproveLoadingStatus.asReadonly();
  public readonly agentUpdateLoadingStatus: Signal<LoadingState> =
    this._agentUpdateLoadingStatus.asReadonly();
  public readonly agentByIdStatusLoadingStatus: Signal<LoadingState> =
    this._agentByIdStatusLoadingStatus.asReadonly();
  public readonly agentStatusesLoadingStatus: Signal<LoadingState> =
    this._agentStatusesLoadingStatus.asReadonly();

  public readonly totalCount: Signal<number> = this._agentsTotalCount.asReadonly();

  public searchRequest$: BehaviorSubject<SearchAgentsRequest> =
    new BehaviorSubject<SearchAgentsRequest>({
      skip: 0,
      take: DEFAULT_TABLE_PAGE_SIZE,
      includeTotalCount: true,
    });

  public readonly statuses$ = defer(() => {
    this._agentStatusesLoadingStatus.set(LoadingState.LOADING);
    return this.statusClient.v1AgentStatusGet();
  }).pipe(
    map((response) =>
      response.map((item) => ({
        id: item.id,
        name: this.translateService.instant(
          'monitoring.agents.statuses.' + item.code.toLocaleLowerCase(),
        ),
      })),
    ),
    catchError((err) => {
      this.toastService.error({
        message: this.translateService.instant(
          'monitoring.agents.toast.errors.agents-statuses-fetch-error',
          { error: outputError(err, this.translateService.instant('errors.unknown')) },
        ),
      });

      return of([]);
    }),
    finalize(() => this._agentStatusesLoadingStatus.set(LoadingState.READY)),
    shareReplay(1),
  );

  private getAgents$ = this.searchRequest$.pipe(
    tap(() => this._agentsLoadingState.set(LoadingState.LOADING)),
    switchMap((request) =>
      this.client.v1AgentSearchPost(request).pipe(
        catchError((err) => {
          this.toastService.error({
            message: this.translateService.instant(
              'monitoring.agents.toast.errors.agents-list-fetch-error',
              { error: outputError(err, this.translateService.instant('errors.unknown')) },
            ),
          });

          this._agentsTotalCount.set(0);
          return of({ items: [], totalCount: 0 });
        }),
      ),
    ),
    tap(({ totalCount }) => {
      this._agentsTotalCount.set(totalCount ?? 0);
      this._agentsLoadingState.set(LoadingState.READY);
    }),
  );

  public readonly agents$: Observable<ISearchAgentsItem[]> = this.getAgents$.pipe(
    map(({ items }) => items ?? []),
  );

  public getById(id: string): Observable<ISearchAgentsItem | null> {
    this._agentByIdStatusLoadingStatus.set(LoadingState.LOADING);
    return this.client.v1AgentIdGet(id).pipe(
      catchError((error) => {
        this.toastService.error({
          message: this.translateService.instant(
            'monitoring.agents.toast.errors.agent-by-id-update-failed',
            { error: outputError(error, this.translateService.instant('errors.unknown')) },
          ),
        });

        return of(null);
      }),
      finalize(() => this._agentByIdStatusLoadingStatus.set(LoadingState.READY)),
    );
  }

  public updateAgent(body: {
    id: string;
    name?: string;
    facilityId: string;
    comment?: string;
  }): Observable<boolean> {
    const request: UpdateAgentRequest = {
      id: body.id,
      name: body.name ?? '',
      facilityId: body.facilityId,
      comment: body.comment ?? null,
    };

    this._agentUpdateLoadingStatus.set(LoadingState.LOADING);
    return this.client.v1AgentPut(request).pipe(
      map(() => true),
      catchError((error) => {
        this.toastService.error({
          message: this.translateService.instant(
            'monitoring.agents.toast.errors.agent-update-failed',
            { error: outputError(error, this.translateService.instant('errors.unknown')) },
          ),
        });

        return of(false);
      }),
      finalize(() => this._agentUpdateLoadingStatus.set(LoadingState.READY)),
    );
  }

  public approveAgent(request: ApproveAgentRequest): Observable<boolean> {
    this._agentApproveLoadingStatus.set(LoadingState.LOADING);
    return this.client.v1AgentApprovePut(request).pipe(
      map(() => true),
      catchError((error) => {
        this.toastService.error({
          message: this.translateService.instant(
            'monitoring.agents.toast.errors.agent-approve-failed',
            { error: outputError(error, this.translateService.instant('errors.unknown')) },
          ),
        });

        return of(false);
      }),
      finalize(() => this._agentApproveLoadingStatus.set(LoadingState.READY)),
    );
  }

  public deleteAgent(id: string): Observable<boolean> {
    this._agentDeleteLoadingStatus.set(LoadingState.LOADING);
    return this.client.v1AgentIdDelete(id).pipe(
      map(() => true),
      catchError((error) => {
        this.toastService.error({
          message: this.translateService.instant(
            'monitoring.agents.toast.errors.agent-delete-failed',
            { error: outputError(error, this.translateService.instant('errors.unknown')) },
          ),
        });

        return of(false);
      }),
      finalize(() => this._agentDeleteLoadingStatus.set(LoadingState.READY)),
    );
  }

  public reloadAgents(): void {
    this.searchRequest$.next({
      ...this.searchRequest$.value,
    });
  }

  public updateAgentRequest(params: Partial<SearchAgentsRequest>): void {
    this.searchRequest$.next({
      ...this.searchRequest$.value,
      ...params,
    });
  }
}
